import { EyeOutlined } from '@ant-design/icons';
import {
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  Modal,
  notification,
  Row,
  Space,
  Table,
  Typography,
} from 'antd';
import React, { useEffect, useState } from 'react';
import { Container } from '../../components/Bloco/styles';
import CaixaDeFiltros from '../../components/CaixaDeFiltros';
import { formatarData } from '../../helpers/utils';
import api from '../../services/api';

const PAGINACAO_INICIAL = { current: 1, pageSize: 10 };
const { RangePicker } = DatePicker;

const formatarConteudo = conteudo => {
  if (!conteudo) return '';

  try {
    return JSON.stringify(JSON.parse(conteudo), null, 2);
  } catch (error) {
    return conteudo;
  }
};

const resumir = conteudo => {
  if (!conteudo) return '-';
  return conteudo.length > 120 ? `${conteudo.substring(0, 120)}...` : conteudo;
};

export function LogsIntegracao() {
  const [form] = Form.useForm();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [logSelecionado, setLogSelecionado] = useState(null);
  const [paginacao, setPaginacao] = useState(PAGINACAO_INICIAL);

  const consultar = async (novaPaginacao = PAGINACAO_INICIAL) => {
    setLoading(true);

    try {
      const filtros = form.getFieldsValue();
      const { data } = await api.get('/LogsRequest', {
        params: {
          DataInicio: filtros.periodo?.[0]
            ?.clone().startOf('day').toISOString(),
          DataFim: filtros.periodo?.[1]
            ?.clone().endOf('day').toISOString(),
          Endpoint: filtros.endpoint || undefined,
          TextoRequest: filtros.textoRequest || undefined,
          TextoResponse: filtros.textoResponse || undefined,
          Page: novaPaginacao.current,
          NumeroRegistros: novaPaginacao.pageSize,
        },
      });

      setLogs((data?.items || []).map(log => ({ ...log, key: log.id })));
      setPaginacao({
        current: data?.pageIndex || novaPaginacao.current,
        pageSize: data?.pageSize || novaPaginacao.pageSize,
        total: data?.count || 0,
      });
    } catch (error) {
      notification.error({
        description: 'Erro ao consultar os logs de integração.',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    consultar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const limparFiltros = () => {
    form.resetFields();
    consultar(PAGINACAO_INICIAL);
  };

  const colunas = [
    {
      title: 'Data',
      dataIndex: 'dataCriacao',
      key: 'dataCriacao',
      width: 160,
      render: valor => formatarData(valor),
    },
    {
      title: 'Endpoint',
      dataIndex: 'endpoint',
      key: 'endpoint',
      width: 300,
      render: resumir,
    },
    {
      title: 'Request',
      dataIndex: 'request',
      key: 'request',
      render: resumir,
    },
    {
      title: 'Response',
      dataIndex: 'response',
      key: 'response',
      render: resumir,
    },
    {
      title: 'Ações',
      key: 'acoes',
      width: 90,
      align: 'center',
      render: (_, registro) => (
        <Button
          aria-label="Visualizar log"
          icon={<EyeOutlined />}
          onClick={() => setLogSelecionado(registro)}
        />
      ),
    },
  ];

  return (
    <>
      <Container>
        <CaixaDeFiltros>
          <Form layout="vertical" form={form} size="large">
            <Row gutter={[24, 0]}>
              <Col xs={24} md={12} lg={6}>
                <Form.Item name="periodo" label="Período">
                  <RangePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12} lg={6}>
                <Form.Item name="endpoint" label="Endpoint">
                  <Input placeholder="Parte do endpoint" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12} lg={6}>
                <Form.Item name="textoRequest" label="Contém no request">
                  <Input placeholder="Texto do request" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12} lg={6}>
                <Form.Item name="textoResponse" label="Contém no response">
                  <Input placeholder="Texto do response" />
                </Form.Item>
              </Col>
            </Row>
            <Row justify="end">
              <Space>
                <Button onClick={limparFiltros}>Limpar</Button>
                <Button
                  type="primary"
                  onClick={() => consultar(PAGINACAO_INICIAL)}
                >
                  Filtrar
                </Button>
              </Space>
            </Row>
          </Form>
        </CaixaDeFiltros>
      </Container>
      <Container>
        <Table
          columns={colunas}
          dataSource={logs}
          loading={loading}
          scroll={{ x: 1000 }}
          pagination={{
            ...paginacao,
            showQuickJumper: true,
            showSizeChanger: true,
            showTotal: total =>
              `Total de ${total} ${total === 1 ? 'item' : 'itens'}`,
          }}
          onChange={consultar}
        />
      </Container>
      <Modal
        title="Detalhes do log de integração"
        open={!!logSelecionado}
        width="90%"
        footer={null}
        onCancel={() => setLogSelecionado(null)}
        styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}
      >
        <Typography.Paragraph>
          <Typography.Text strong>Endpoint: </Typography.Text>
          {logSelecionado?.endpoint}
        </Typography.Paragraph>
        <Typography.Paragraph>
          <Typography.Text strong>Data: </Typography.Text>
          {formatarData(logSelecionado?.dataCriacao)}
        </Typography.Paragraph>
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={12}>
            <Card title="Request">
              <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {formatarConteudo(logSelecionado?.request)}
              </pre>
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card title="Response">
              <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {formatarConteudo(logSelecionado?.response)}
              </pre>
            </Card>
          </Col>
        </Row>
      </Modal>
    </>
  );
}
